import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatError(detail) {
  if (!detail) return "Ein Fehler ist aufgetreten.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#1A1A2E] text-[#F0EDE8] focus:border-[#C9A84C] focus:outline-none py-4 text-sm placeholder-[#9A9A9A]/40 transition-colors duration-300";

  return (
    <div className="min-h-screen bg-[#050510] flex items-center justify-center px-6">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <div className="text-center mb-10">
          <h1
            className="text-4xl font-light text-[#C9A84C] tracking-[0.2em] mb-2"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            AURALIA
          </h1>
          <p className="text-[0.65rem] uppercase tracking-[0.35em] text-[#9A9A9A]">
            Admin-Bereich
          </p>
        </div>

        <div className="bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl p-10">
          <h2
            className="text-2xl font-light text-[#F0EDE8] mb-8 text-center"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Anmelden
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#C9A84C] mb-2">
                <Mail size={13} /> E-Mail
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@auralia.de"
                className={inputClass}
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#C9A84C] mb-2">
                <Lock size={13} /> Passwort
              </label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${inputClass} pr-10`}
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#9A9A9A] hover:text-[#F0EDE8] transition-colors"
                  onClick={() => setShowPass(!showPass)}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-xs" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-gold-shimmer border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.2em] py-4 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300 disabled:opacity-40 mt-4"
              data-testid="admin-login-submit"
            >
              {submitting ? "Anmelden ..." : "Anmelden"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
